import DataLoader from 'dataloader';
import { type Environment, fetchQuery, graphql } from 'react-relay';

import type { getUsersLoaderQuery } from './__generated__/getUsersLoaderQuery.graphql';

export type HydratedUser = {
	accountId: string;
	ari: string;
	avatarUrl: string;
	name: string;
};

// Module-level singleton per environment
const hydratedUserDataLoader: WeakMap<
	Environment,
	DataLoader<string, HydratedUser | null>
> = new WeakMap();

export function getUsersLoader(
	environment: Environment,
): DataLoader<string, HydratedUser | null, string> | undefined {
	if (hydratedUserDataLoader.get(environment)) {
		return hydratedUserDataLoader.get(environment);
	}

	const loader = new DataLoader<string, HydratedUser | null>(
		async (accountIds) => {
			const data = await fetchQuery<getUsersLoaderQuery>(
				environment,
				graphql`
					query getUsersLoaderQuery($accountIds: [ID!]!) {
						users(accountIds: $accountIds) {
							id
							accountId
							name
							picture
						}
					}
				`,
				{ accountIds },
				{ fetchPolicy: 'store-or-network' },
			).toPromise();

			// Map results to ensure O(1) lookup
			const userMap = new Map<string, HydratedUser>();
			const ariMap = new Map<string, HydratedUser>();
			for (const user of data?.users ?? []) {
				const mappedUser = {
					ari: user?.id,
					accountId: user?.accountId,
					name: user?.name,
					avatarUrl: user?.picture,
				};
				userMap.set(user.accountId, mappedUser);
				ariMap.set(mappedUser.ari, mappedUser);
			}

			// Return values in the exact same order as 'keys', as expected by the DataLoader
			return accountIds.map((accountId) => {
				return userMap.get(accountId) ?? ariMap.get(accountId) ?? null;
			});
		},
		{
			cache: true,
			batch: true,
		},
	);

	hydratedUserDataLoader.set(environment, loader);

	return loader;
}
